import { ChangeDetectionStrategy, Component, OnDestroy, signal, ViewEncapsulation } from "@angular/core";
import { DemoInstancesService } from "../services/demo-instances.service";
import { combineLatest, map, of, startWith, Subject, switchMap } from "rxjs";
import { LimitService } from "../services/limit.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MetadataService } from "../services/metadata.service";
import { UserService } from "../services/user.service";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import { PollingService } from "../services/poll.service";

@Component({
  selector: "brd-home",
  templateUrl: "./home.component.html",
  styleUrl: "./home.component.less",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HomeComponent implements OnDestroy {
  gridView = signal(true);
  loading = signal(false);

  private instances$ = combineLatest([this.demoInstanceService.homeDemos$, this.userService.userInfo$]).pipe(
    takeUntilDestroyed(),
    map(([instances, userInfo]) => {
      this.loading.set(false);
      return instances.map(x => this.demoInstanceService.mapDemoInstance(x, userInfo?.username ?? "", this.userService.isSuperUser()));
    })
  );

  private selectedGroupSubject = new Subject<string | undefined>();
  private selectedGroup$ = this.selectedGroupSubject.asObservable().pipe(
    takeUntilDestroyed(),
    switchMap(res => combineLatest([this.limitService.getLimit(res), this.demoInstanceService.getHomeDemos(res), of(res)])),
    map(([, , res]) => res)
  );

  private groups$ = this.userService.internalGroups$.pipe(
    map(res =>
      res.map(x => ({
        ...x,
        text: x.default ? `${x.groupName} - Default` : x.groupName
      }))
    )
  );

  homeData$ = combineLatest([this.limitService.limits$, this.groups$, this.instances$, this.selectedGroup$]).pipe(
    takeUntilDestroyed(),
    map(([limits, groups, instances, selectedGroup]) => {
      const group = groups.find(x => x.groupName === selectedGroup);
      return {
        limits,
        groups,
        instances,
        selectedGroup,
        instancesQuotaExceeded: (limits?.currentUsage ?? 0) >= (limits?.limit ?? 0),
        defaultGroup: group?.default ?? true
      };
    }),
    startWith({
      limits: {
        limit: 0,
        currentUsage: 0,
        group: ""
      },
      groups: [],
      instances: [],
      instancesQuotaExceeded: true,
      defaultGroup: false,
      selectedGroup: ""
    })
  );

  constructor(
    private demoInstanceService: DemoInstancesService,
    private limitService: LimitService,
    private metadataService: MetadataService,
    private userService: UserService,
    private pollService: PollingService
  ) {
    this.metadataService.getLatestVersion().subscribe();
    this.loading.set(true);
    this.userService
      .getInternalGroups()
      .pipe(
        takeUntilDestroyed(),
        map(res => {
          this.selectedGroupSubject.next(res?.find(x => x.default)?.groupName ?? "");
          return res;
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.pollService.stopPoll();
  }

  toggleView(): void {
    this.gridView.update(x => !x);
  }

  createNewDemoClick(): void {
    document.getElementsByClassName("demo-section")[0].scrollIntoView({ behavior: "smooth" });
  }

  onGroupChanged({ value, event }: ValueChangedEvent): void {
    if (!event) return;

    this.pollService.stopPoll();
    this.loading.set(true);
    this.selectedGroupSubject.next(value);
  }
}
